import { toastError } from '@/lib/toast-error';
import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Pencil, RotateCcw, Save, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const LANGS = ['es', 'en', 'pt'] as const;
type Lang = (typeof LANGS)[number];

function PreviewFrame({ subject, html }: { subject: string; html: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <p className="text-sm">
        <span className="text-muted-foreground">{t('users.onboarding_page.preview.subject')}: </span>
        <span className="font-medium">{subject}</span>
      </p>
      <iframe
        sandbox=""
        srcDoc={`<body style="margin:16px;background:#ffffff">${html}</body>`}
        className="w-full h-[52vh] rounded-md border bg-white"
        title={subject}
      />
    </div>
  );
}

/**
 * Preview de los mails de onboarding (IT + People). Los de People (migrados del
 * viejo onboarding-people) se editan con el lapicito: asunto + HTML con
 * {{variables}}; el override se guarda en DB y "Restaurar" vuelve al default.
 */
export function EmailPreviewDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.users.getEmailPreviews.useQuery(undefined, { enabled: open });

  // Selección: key de IT ('bienvenida'…) o tipo de People ('manager'…) + idioma
  const [selectedId, setSelectedId] = useState('bienvenida');
  const [lang, setLang] = useState<Lang>('es');

  // Editor
  const [editing, setEditing] = useState(false);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftHtml, setDraftHtml] = useState('');

  const templates = data?.templates ?? [];
  const itTemplates = templates.filter((tp) => tp.group === 'it');
  const peopleTypes = useMemo(() => {
    const seen = new Map<string, string>();
    for (const tp of templates) {
      if (tp.group === 'people') {
        const type = tp.key.replace(/_(es|en|pt)$/, '');
        if (!seen.has(type)) seen.set(type, tp.label);
      }
    }
    return [...seen.entries()];
  }, [templates]);

  const isPeopleSelection = peopleTypes.some(([type]) => type === selectedId);
  const current = isPeopleSelection
    ? templates.find((tp) => tp.key === `${selectedId}_${lang}`)
    : templates.find((tp) => tp.key === selectedId);

  const save = trpc.users.saveOnboardingMailTemplate.useMutation({
    onSuccess: () => {
      toast.success(t('users.emailpreviewdialog.template_guardado'));
      utils.users.getEmailPreviews.invalidate();
      setEditing(false);
    },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar'),
  });
  const reset = trpc.users.resetOnboardingMailTemplate.useMutation({
    onSuccess: () => {
      toast.success(t('users.emailpreviewdialog.template_restaurado_al_default'));
      utils.users.getEmailPreviews.invalidate();
      setEditing(false);
    },
    onError: (e) => toastError(e, 'common.toast.error_al_restaurar'),
  });

  const startEdit = () => {
    if (!current) return;
    setDraftSubject((current as any).rawSubject ?? current.subject);
    setDraftHtml((current as any).rawHtml ?? current.html);
    setEditing(true);
  };

  const handleClose = () => {
    setEditing(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('users.onboarding_page.preview.title')}</DialogTitle>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="flex items-center justify-center p-16">
            <WinclapLoader size={32} className="text-foreground/25" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Selector de template + idioma + acciones */}
            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={selectedId}
                onValueChange={(v) => { setSelectedId(v); setEditing(false); }}
              >
                <SelectTrigger className="w-[290px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>IT</SelectLabel>
                    {itTemplates.map((tp) => (
                      <SelectItem key={tp.key} value={tp.key}>{tp.label}</SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>People</SelectLabel>
                    {peopleTypes.map(([type, label]) => (
                      <SelectItem key={type} value={type}>{label}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              {isPeopleSelection && (
                <div className="flex gap-1">
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      onClick={() => { setLang(l); setEditing(false); }}
                      className={cn(
                        'px-2.5 py-1 rounded-md text-xs font-medium uppercase transition-colors',
                        lang === l ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 ml-auto">
                {current?.isOverride && (
                  <Badge variant="secondary" className="text-[10px]">personalizado</Badge>
                )}
                {current?.editable && !editing && (
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={startEdit}>
                    <Pencil className="h-3.5 w-3.5" /> Editar
                  </Button>
                )}
                {current?.editable && current?.isOverride && !editing && (
                  <Button
                    size="sm" variant="ghost" className="h-8 gap-1.5 text-muted-foreground"
                    disabled={reset.isPending}
                    onClick={() => reset.mutate({ templateKey: current.key })}
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> {t('users.emailpreviewdialog.restaurar_default')}
                  </Button>
                )}
              </div>
            </div>

            {!current ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t('users.emailpreviewdialog.template_no_encontrado')}</p>
            ) : editing ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">Asunto</p>
                  <Input value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{t('users.emailpreviewdialog.cuerpo_html')}</p>
                    <Textarea
                      value={draftHtml}
                      onChange={(e) => setDraftHtml(e.target.value)}
                      rows={18}
                      className="font-mono text-xs resize-none"
                    />
                    {current.variables.length > 0 && (
                      <p className="text-[11px] text-muted-foreground">
                        Variables: {current.variables.map((v) => `{{${v}}}`).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{t('users.emailpreviewdialog.vista_previa_en_vivo')}</p>
                    <iframe
                      sandbox=""
                      srcDoc={`<body style="margin:16px;background:#ffffff">${draftHtml}</body>`}
                      className="w-full h-[46vh] rounded-md border bg-white"
                      title="preview"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditing(false)}>
                    <X className="h-3.5 w-3.5" /> Cancelar
                  </Button>
                  <Button
                    size="sm" className="gap-1.5"
                    disabled={save.isPending || !draftSubject.trim() || draftHtml.trim().length < 10}
                    onClick={() => save.mutate({ templateKey: current.key, subject: draftSubject, html: draftHtml })}
                  >
                    {save.isPending ? <WinclapLoader size={14} /> : <Save className="h-3.5 w-3.5" />} Guardar
                  </Button>
                </div>
              </div>
            ) : (
              <PreviewFrame subject={current.subject} html={current.html} />
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t('users.onboarding_page.preview.note')}</p>
      </DialogContent>
    </Dialog>
  );
}
